import { redactJotformText, isJotformSensitiveKey } from './jotform-privacy';

const EXACT_SENSITIVE_KEYS = new Set([
  'invitation',
  'invitationhash',
  'authorization',
  'adminpin',
  'card',
  'cardnumber',
  'cookie',
  'cvc',
  'cvv',
  'dateofbirth',
  'dob',
  'ein',
  'federalein',
  'operatorpin',
  'otp',
  'pan',
  'pairingcode',
  'pc',
  'passcode',
  'pin',
  'rawtoken',
  'recaptchatoken',
  'resetcode',
  'sessiontoken',
  'setcookie',
  'ssn',
  'taxid',
  'taxidentificationnumber',
  'verificationcode',
  'xpointcraftoperatorsession',
]);

const SENSITIVE_KEY_FRAGMENTS = [
  'accesstoken',
  'apikey',
  'authtoken',
  'bankaccount',
  'cardnumber',
  'clientsecret',
  'operatorSessionToken'.toLowerCase(),
  'password',
  'privatekey',
  'refreshtoken',
  'routingnumber',
  'socialsecurity',
  'secret',
  'token',
];

const REDACTED = '[REDACTED]';

/** No diagnostic capture of anonymous capabilities or authenticated send payloads. */
export function isPaymentLinkApiUrl(value: string): boolean {
  try {
    const path = new URL(value, 'https://pointcraft.invalid').pathname;
    return /^\/(?:b\/)?api\/(?:public\/(?:payment-links|customer-payment-confirmation)(?:\/|$)|business\/customers\/[^/]+\/payments(?:\/|$)|sales\/payment\/link\/requests(?:\/|$))/.test(
      path
    );
  } catch {
    return false;
  }
}

/** Invitations are capabilities even when embedded in an error or navigation URL. */
export function redactPaymentLinkText(value: string, replacement: string): string {
  return value
    .replace(/(\/confirm-payment(?:\?[^#\s"'<>]*)?#)[^\s"'<>]+/gi, `$1${replacement}`)
    .replace(
      /(\/(?:payment-link|(?:b\/)?api\/public\/payment-links)\/)[^/?#\s"'<>]+/gi,
      `$1${replacement}`
    )
    .replace(
      /\b(invitation(?:Hash)?)\b(\s*["']?\s*[:=]\s*)("(?:\\.|[^"\\])*"?|'(?:\\.|[^'\\])*'?|[^\s,;&}\]"']+)/gi,
      (_match, key: string, separator: string, secret: string) => {
        const quote = ['"', "'"].includes(secret[0]) ? secret[0] : '';
        return `${key}${separator}${quote}${replacement}${quote}`;
      }
    );
}

export function isSensitiveDataKey(key: string): boolean {
  const normalized = key.toLowerCase().replaceAll('_', '').replaceAll('-', '');

  return (
    EXACT_SENSITIVE_KEYS.has(normalized) ||
    isJotformSensitiveKey(key) ||
    SENSITIVE_KEY_FRAGMENTS.some((fragment) => normalized.includes(fragment))
  );
}

function redactSensitiveString(value: string): string {
  return redactPaymentLinkText(redactJotformText(value, REDACTED), REDACTED)
    .replace(
      /([?&](?:pc|pairingCode|pin|resetCode|accountNumber|identifier|operatorSessionToken|sessionToken|rawToken|(?:setup_intent_|payment_intent_)?client_secret|X-PointCraft-Operator-Session)=)[^&#\s]*/gi,
      `$1${REDACTED}`
    )
    .replace(
      /(["'](?:pc|pairingCode|pin|resetCode|accountNumber|identifier|operatorSessionToken|sessionToken|rawToken|X-PointCraft-Operator-Session)["']\s*:\s*)["'][^"']*["']/gi,
      `$1"${REDACTED}"`
    );
}

/**
 * Clone diagnostic payloads while removing credentials and raw PIN values.
 * The implementation is cycle-safe so it can be used directly by Sentry hooks.
 */
export function redactSensitiveData<T>(value: T, seen = new WeakMap<object, unknown>()): T {
  if (typeof value === 'string') {
    return redactSensitiveString(value) as T;
  }

  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (value instanceof Date) {
    return value;
  }

  const cached = seen.get(value);
  if (cached) return cached as T;

  if (Array.isArray(value)) {
    const redacted: unknown[] = [];
    seen.set(value, redacted);
    value.forEach((item) => redacted.push(redactSensitiveData(item, seen)));
    return redacted as T;
  }

  const redacted: Record<string, unknown> = {};
  seen.set(value, redacted);

  Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
    redacted[key] = isSensitiveDataKey(key) ? REDACTED : redactSensitiveData(item, seen);
  });

  return redacted as T;
}
